import React, { useState, useEffect } from 'react';
import { Box, Container, Heading, Text, Flex, Button, VStack, IconButton } from '@chakra-ui/react';
import { FaCheckCircle, FaDownload } from 'react-icons/fa';
import CorporateStepper from './CorporateStepper';
import CompanyInformation from './Steps/CompanyInformation';
import CorporateOfficers from './Steps/CorporateOfficers';
import DocumentsVerification from './Steps/DocumentsVerification';
import BankAccounts from './Steps/BankAccounts';
import { useDispatch, useSelector } from 'react-redux';
import { submitCorporateStep } from '../../Redux/Action/corporateSignup/corporateSignupActions';
import { downloadCorporateSignupPDF } from '../../Utils/pdfGenerator';

const CorporateSignupFlow = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [countdown, setCountdown] = useState(90);
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.CorporateSignup);

  useEffect(() => {
    // Load persisted state on mount
    const savedStep = localStorage.getItem('activeStep');
    const savedData = localStorage.getItem('corporateSignupData');
    
    if (savedStep) {
        setActiveStep(parseInt(savedStep));
    }
    if (savedData) {
        try {
            setFormData(JSON.parse(savedData));
        } catch (e) {
            console.error("Failed to parse saved form data", e);
        }
    }
  }, []);

  useEffect(() => {
    let timer;
    if (isCompleted) {
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        } else {
            window.location.href = '/login';
        }
    }
    return () => clearInterval(timer);
  }, [isCompleted, countdown]);

  const steps = [
    { title: 'Company Information', description: 'Basic & Financial Details' },
    { title: 'Corporate Officers', description: 'Directors & Shareholders' },
    { title: 'Documents Verification', description: 'Upload Required Documents' },
    { title: 'Bank Accounts', description: 'Bank Details' },
  ];

  const handleNext = (stepData) => {
    // Update local state with new data
    const updatedFormData = { ...formData, [activeStep]: stepData };
    setFormData(updatedFormData);

    // Prepare data for localStorage (sanitize files)
    const dataToSave = { ...updatedFormData };
    if (activeStep === 2 && stepData.documents) {
        // For documents step, store metadata instead of File objects
        dataToSave[2] = {
            ...stepData,
            documents: stepData.documents.map(doc => ({
                ...doc,
                file: doc.file instanceof File ? { name: doc.file.name, type: 'mock' } : doc.file
            }))
        };
    }
    localStorage.setItem('corporateSignupData', JSON.stringify(dataToSave));

    let payload;
    
    if (activeStep === 2) {
        // Step 3: Documents - Use FormData
        const formDataPayload = new FormData();
        formDataPayload.append('step', activeStep + 1);
        
        const storedEmail = localStorage.getItem('corporateEmail');
        const storedName = localStorage.getItem('corporateName');
        
        if (storedEmail) formDataPayload.append('email', storedEmail);
        if (storedName) formDataPayload.append('corporateName', storedName);

        if (stepData.documents && Array.isArray(stepData.documents)) {
             stepData.documents.forEach((doc, index) => {
                 formDataPayload.append(`documents[${index}][documentType]`, doc.documentType || '');
                 formDataPayload.append(`documents[${index}][documentNo]`, doc.documentNo || '');
                 formDataPayload.append(`documents[${index}][expiryDate]`, doc.expiryDate || '');
                 
                 // Only append if it's a real File object
                 if (doc.file instanceof File) {
                     formDataPayload.append(`documents[${index}][file]`, doc.file);
                 }
             });
        }
        payload = formDataPayload;
    } else {
        // Standard JSON payload
        payload = {
            step: activeStep + 1,
            ...stepData
        };

        if (activeStep > 0) {
            const storedEmail = localStorage.getItem('corporateEmail');
            const storedName = localStorage.getItem('corporateName');
            
            if (storedEmail) payload.email = storedEmail;
            if (storedName) payload.corporateName = storedName;
        }

        if (activeStep === 0) {
            if (stepData.email) localStorage.setItem('corporateEmail', stepData.email);
            if (stepData.corporateName) localStorage.setItem('corporateName', stepData.corporateName);
        }
    }

    dispatch(submitCorporateStep(payload, () => {
        // Callback on success
        if (activeStep < steps.length - 1) {
            const nextStep = activeStep + 1;
            setActiveStep(nextStep);
            localStorage.setItem('activeStep', nextStep.toString());
            window.scrollTo(0, 0);
        } else {
             // Final step completion
             localStorage.removeItem('corporateEmail');
             localStorage.removeItem('corporateName');
             localStorage.removeItem('corporateSignupData'); // Clear form data
             localStorage.removeItem('activeStep'); // Clear step
             setIsCompleted(true);
             window.scrollTo(0, 0);
        }
    }));
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const renderStepContent = (step) => {
    const stepProps = {
        onNext: handleNext,
        loading: loading,
        initialValues: formData[step]
    };

    switch (step) {
      case 0:
        return <CompanyInformation {...stepProps} />;
      case 1:
        return <CorporateOfficers {...stepProps} onBack={handleBack} />;
      case 2:
        return <DocumentsVerification {...stepProps} onBack={handleBack} />;
      case 3:
        return <BankAccounts {...stepProps} onBack={handleBack} />;
      default:
        return <CompanyInformation {...stepProps} />;
    }
  };

  if (isCompleted) {
    return (
        <Box minH="100vh" bgGradient="linear(to-br, gray.50, blue.50)" py={12} display="flex" alignItems="center" justifyContent="center">
             <Container maxW="md">
                <Box 
                    bg="white" 
                    p={10} 
                    borderRadius="xl" 
                    shadow="2xl" 
                    textAlign="center"
                    borderTop="6px solid"
                    borderTopColor="green.500"
                >
                    <VStack spacing={6}>
                        <FaCheckCircle size="60px" color="#38A169" />
                        <Heading size="xl" color="gray.800">Application Submitted!</Heading>
                        <Text color="gray.600" fontSize="lg">
                            Thank you for submitting your corporate account application. We are currently verifying your documents.
                        </Text>
                        <Box bg="blue.50" p={4} borderRadius="md" width="100%">
                             <Text fontSize="sm" color="blue.700" fontWeight="medium">
                                 Redirecting to Login in {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                             </Text>
                        </Box>
                        <Button 
                            colorPalette="blue" 
                            width="full" 
                            size="lg"
                            onClick={() => window.location.href = '/login'}
                        >
                            Back to Login
                        </Button>
                    </VStack>
                </Box>
             </Container>
        </Box>
    );
  }

  return (
    <Box minH="100vh" bgGradient="linear(to-br, gray.50, blue.50)" py={12}>
      <Container maxW="container.xl">
        <Flex direction="column" gap={10}>
            <Box textAlign="center" mb={6}>
                <Heading as="h1" size="2xl" color="blue.700" mb={3} fontWeight="800" letterSpacing="tight">
                    Corporate Signup
                </Heading>
                <Text color="gray.600" fontSize="lg" maxW="2xl" mx="auto">
                    Please provide your company details to establish your corporate trading account with us.
                </Text>
            </Box>

          <CorporateStepper steps={steps} activeStep={activeStep} />

          <Box 
            bg="white" 
            p={{ base: 6, md: 10 }} 
            borderRadius="xl" 
            shadow="xl" 
            border="1px" 
            borderColor="gray.100"
            position="relative"
            _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "6px",
                bgGradient: "linear(to-r, blue.400, purple.500)",
                borderTopRadius: "xl"
            }}
          >
            {/* Download Form Button */}
            <Button
              position="absolute"
              top="-15px"
              right="20px"
              size="md"
              colorScheme="blue"
              borderRadius="md"
              boxShadow="lg"
              leftIcon={<FaDownload />}
              onClick={() => downloadCorporateSignupPDF(formData)}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'xl',
                bg: 'blue.600'
              }}
              transition="all 0.2s"
              fontWeight="semibold"
              px={4}
              py={2}
            >
              Download Complete Form
            </Button>
            
            {renderStepContent(activeStep)}
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default CorporateSignupFlow;
